import React from "react";
import { Box, Paper, Skeleton, CircularProgress, Typography, Grid } from "@mui/material";

/**
 * Reusable loading state component with skeleton placeholders.
 *
 * @param {string} message - Loading message to display
 * @param {'page'|'card'|'inline'} variant - Display variant
 * @param {number} skeletonCount - Number of skeleton cards (page variant)
 */
const LoadingState = ({ message = "Laden...", variant = "page", skeletonCount = 4 }) => {
  if (variant === "inline") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
        <CircularProgress size={20} sx={{ color: "#6366f1" }} />
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
    );
  }

  if (variant === "card") {
    return (
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 2, border: "1px solid #e5e7eb" }}
      >
        <Skeleton variant="text" width="40%" height={28} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="55%" height={20} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
      </Paper>
    );
  }

  // page variant
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* Header skeleton */}
      <Skeleton
        variant="rectangular"
        height={80}
        sx={{ mb: 3, borderRadius: 2 }}
      />

      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Array.from({ length: Math.min(skeletonCount, 4) }).map((_, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Skeleton
              variant="rectangular"
              height={80}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Content cards */}
      <Grid container spacing={2}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Grid item xs={12} sm={6} md={4} key={`card-${i}`}>
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb" }}
            >
              <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={18} />
              <Skeleton variant="text" width="45%" height={18} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Loading message */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          mt: 3,
        }}
      >
        <CircularProgress size={20} sx={{ color: "#6366f1" }} />
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
    </Box>
  );
};

export default LoadingState;
